import {config, useTransition} from 'react-spring';

import {HaNSensorInformationProps} from './types';
import {useAppSelector, useFeatureToggle} from '@hooks';
import {getIsMeetingsCalendarEnabled, getIsOccupancyEnabled} from '@lib/store';
import {FlexCol, FlexRow, H1, H3, H4, P} from '@quarks';
import {MeetingRoomScheduleData, HaNModalHeader} from '@organisms';
import {useTranslation} from 'react-i18next';
import {SensorInformationWrapper, StyledContainer} from './styles';
import {MapIndicatorIllustration, MapIndicatorKey} from './MapIndicatorIllustration';

/**
 * Lists available rooms (horizontally) in Here And Now
 */

export const HaNSensorInformation = ({onBack, onClose, page}: HaNSensorInformationProps) => {
  const {t} = useTranslation();
  const isOccupancyEnabled = useAppSelector(getIsOccupancyEnabled);
  const isMeetingCalendarEnabled = useAppSelector(getIsMeetingsCalendarEnabled);
  const {HybridMeetingsCalendarView} = useFeatureToggle();
  const transition = useTransition(page === 'sensorInformation', {
    from: {transform: 'translate3d(100%, 0, 0)'},
    enter: {transform: 'translate3d(0%, 0, 0)'},
    leave: {transform: 'translate3d(100%, 0, 0)'},
    config: {...config.stiff, clamp: true},
  });

  return transition(
    (style, item) =>
      item && (
        <SensorInformationWrapper style={style}>
          <HaNModalHeader
            onBack={onBack}
            onClose={onClose}
            title={t('screen:MeetingRoomDetails')}
          />
          <StyledContainer>
            <H1>{t('AvailabilityInformation')}</H1>

            <FlexCol gap={24}>
              <H3 className="p-0">{t('hereAndNow:MapIndicatorInformation')}</H3>

              <MapIndicatorRows />
            </FlexCol>

            {isOccupancyEnabled && (
              <FlexCol gap={24}>
                <H3 className="p-0">{t('hereAndNow:SensorInformation')}</H3>
                <FlexCol gap={8}>
                  <MeetingRoomScheduleData
                    data-testid="SensorInformationSensorIconAvailable"
                    status="success"
                    title={t('hereAndNow:RoomSeemsToBeAvailableTitle')}
                    icon="broadcast"
                  />
                  <P>{t('hereAndNow:EmptySensorDescription')}</P>
                </FlexCol>
                <FlexCol gap={8}>
                  <MeetingRoomScheduleData
                    data-testid="SensorInformationSensorIconUnavailable"
                    status="error"
                    title={t('hereAndNow:RoomSeemsToBeInUseTitle')}
                    icon="broadcast"
                  />
                  <P>{t('hereAndNow:SensorInUseDescription')}</P>
                </FlexCol>
                <FlexCol gap={8}>
                  <MeetingRoomScheduleData
                    data-testid="SensorInformationSensorIconUnknown"
                    status="unknown"
                    title={t('hereAndNow:RoomLiveDataUnavailableTitle')}
                    icon="broadcast"
                  />
                  <P>{t('hereAndNow:NoLiveDataDescription')}</P>
                </FlexCol>
                <FlexCol gap={8}>
                  <MeetingRoomScheduleData
                    data-testid="SensorInformationSensorIconNoSensor"
                    status={undefined}
                    title={t('NoSensorInstalled')}
                    icon="noBroadcast"
                  />
                  <P>{t('NoSensorInstalledDescriptionRoom')}</P>
                </FlexCol>
              </FlexCol>
            )}
            {isMeetingCalendarEnabled && HybridMeetingsCalendarView && (
              <FlexCol gap={24}>
                <H3 className="p-0">{t('hereAndNow:CalendarInformation')}</H3>
                <FlexCol gap={8}>
                  <MeetingRoomScheduleData
                    data-testid="SensorInformationScheduleIconAvailable"
                    status="success"
                    title={t('NotBooked')}
                    icon="calendar"
                  />
                  <P>{t('hereAndNow:NotBookedDescription')}</P>
                </FlexCol>
                <FlexCol gap={8}>
                  <MeetingRoomScheduleData
                    data-testid="SensorInformationScheduleIconUnavailable"
                    status="error"
                    title={t('Booked')}
                    icon="calendar"
                  />
                  <P>{t('hereAndNow:BookedDescription')}</P>
                </FlexCol>
                <FlexCol gap={8}>
                  <MeetingRoomScheduleData
                    data-testid="SensorInformationScheduleIconAvailableInXMinutes"
                    status="warning"
                    title={t('hereAndNow:AvailableInXMinutes')}
                    icon="calendar"
                  />
                  <P>{t('hereAndNow:AvailableDescription')}</P>
                </FlexCol>
                <FlexCol gap={8}>
                  <MeetingRoomScheduleData
                    data-testid="SensorInformationScheduleIconNextBookingX"
                    status="warning"
                    title={t('hereAndNow:NextBookingX')}
                    icon="calendar"
                  />
                  <P>{t('hereAndNow:NextBookingDescription')}</P>
                </FlexCol>
                <FlexCol gap={8}>
                  <MeetingRoomScheduleData
                    data-testid="SensorInformationScheduleIconBookable"
                    status="unknown"
                    title={t('hereAndNow:Bookable')}
                    icon="calendar"
                  />
                  <P>{t('hereAndNow:BookableDescription')}</P>
                </FlexCol>
                <FlexCol gap={8}>
                  <MeetingRoomScheduleData
                    data-testid="SensorInformationScheduleIconNotBookable"
                    status={undefined}
                    title={t('hereAndNow:AdHocUse')}
                    icon="noCalendar"
                  />
                  <P>{t('hereAndNow:RoomInfoAdHocDetail')}</P>
                </FlexCol>
              </FlexCol>
            )}
          </StyledContainer>
        </SensorInformationWrapper>
      ),
  );
};

const MapIndicatorRows = () => {
  const keys = Object.keys(MapIndicatorIllustration) as MapIndicatorKey[];

  return (
    <>
      {keys.map((variant) => (
        <MapIndicatorRow
          variant={variant}
          key={variant}
        />
      ))}
    </>
  );
};
const MapIndicatorRow = ({variant}: {variant: MapIndicatorKey}) => {
  const Illustration = MapIndicatorIllustration[variant];

  const {t} = useTranslation();

  return (
    <FlexCol gap={8}>
      <FlexRow
        justifyContent="flex-start"
        alignItems="center"
        gap={16}>
        <Illustration className={'shrink-0 w-9'} />
        <H4 fontWeight="bold">{t(`hereAndNow:MapIndicatorRoom${variant}.Title`)}</H4>
      </FlexRow>
      <P>{t(`hereAndNow:MapIndicatorRoom${variant}.Description`)}</P>
    </FlexCol>
  );
};
